import { MisdeliveryBolFormValues } from "../../@hooks/useMisdeliveryBolForm";
import { DateHelper } from "../../helpers";
import {
  EMAIL_VALIDATION,
  EMAIL_TEMPLATE,
  FILE_SIZE_UNITS,
  ERROR_MESSAGES,
} from "./BOLEmail.constants";

// Address Formatting
export const formatAddressLine = (parts: (string | undefined)[]): string =>
  parts.filter(Boolean).join(" ");

export const formatCityStateZip = (
  city?: string,
  state?: string,
  zip?: string
): string => [city, state, zip].filter(Boolean).join(", ").replace(", ,", ",");

// URL Generation
export const getMisdeliveryEntryUrl = (osdNumber: string): string => {
  const baseUrl = globalThis.location?.origin ?? "";
  const safeOsd = (osdNumber ?? "").toString().trim();
  return `${baseUrl}/misdelivery/entry/${encodeURIComponent(safeOsd)}`;
};

// Image Conversion
export const convertImageToBase64 = async (url: string): Promise<string> => {
  const response = await fetch(url);
  const blob = await response.blob();
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

// Email Content Generation
export const getLogoBase64 = (): Promise<string> =>
  convertImageToBase64(`${process.env.PUBLIC_URL}/Saia_ltl.png`);

export const getHazmatEmailContent = async (
  osdNumber: string
): Promise<string> => {
  const safeOsd = (osdNumber ?? "").toString().trim();
  const url = getMisdeliveryEntryUrl(safeOsd);
  const logoBase64 = await getLogoBase64();
  return `
    <img src="${logoBase64}" alt="Logo" />
    <p></p><br />
    <p>Request Approval Hazmat MSD exception #<a href="${url}">${safeOsd}</a></p>
  `;
};

export const getNormalEmailContent = async (
  bol: MisdeliveryBolFormValues
): Promise<string> => {
  const logoBase64 = await getLogoBase64();
  const address = formatAddressLine([bol.pickupAddress1, bol.pickupAddress2]);
  const cityStateZip = formatCityStateZip(
    bol.pickupCity,
    bol.pickupState,
    bol.pickupZip
  );
  const pickupNumber = (bol?.pickupNumber ?? "").toString();

  return EMAIL_TEMPLATE.replace("{{logoBase64}}", logoBase64)
    .replace("{{date}}", DateHelper.todayDate())
    .replace("{{businessName}}", bol.pickupBusinessName ?? "")
    .replace("{{address}}", address)
    .replace("{{cityStateZip}}", cityStateZip)
    .replace("{{pickupNumber}}", pickupNumber);
};

// File Size Formatting
export const formatBytes = (bytes: number): string => {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";

  const unitIndex = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    FILE_SIZE_UNITS.length - 1
  );
  const value = bytes / Math.pow(1024, unitIndex);
  const decimals = unitIndex === 0 ? 0 : 1;

  return `${value.toFixed(decimals)} ${FILE_SIZE_UNITS[unitIndex]}`;
};

// Email Validation
export interface ValidationResult {
  isValid: boolean;
  error: string;
}

export const validateEmails = (
  emails: string,
  required = true
): ValidationResult => {
  const trimmed = emails.trim();

  if (!trimmed) {
    return {
      isValid: !required,
      error: required ? ERROR_MESSAGES.REQUIRED : "",
    };
  }

  if (trimmed.length > EMAIL_VALIDATION.MAX_LENGTH) {
    return { isValid: false, error: ERROR_MESSAGES.MAX_LENGTH };
  }

  const allValid = trimmed
    .split(",")
    .map((email) => email.trim())
    .every((email) => EMAIL_VALIDATION.REGEX.test(email));

  return {
    isValid: allValid,
    error: allValid ? "" : ERROR_MESSAGES.INVALID,
  };
};

// Subject Line Generation
export const getEmailSubject = (
  pickupNumber: string | number | undefined,
  isHazmat: boolean
): string => {
  const puNumber = (pickupNumber ?? "").toString();
  return isHazmat
    ? `Approval for Hazmat MISDELIVERED SHIPMENT PU# ${puNumber}`
    : `MISDELIVERED SHIPMENT PU# ${puNumber}`;
};

// PDF Download Handler
export const downloadFile = (file: File): void => {
  const url = URL.createObjectURL(file);
  const link = document.createElement("a");
  link.href = url;
  link.download = file.name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};
